import { PracticeList } from '@organization/store/organization/PracticeList';
import autobind from 'autobind';
import { PracticeContactViewModel } from 'chaika-types';
import { makeObservable, observable, action } from 'mobx';

import { SelectProps } from '@components/Select';

import { OrganizationData, OrganizationStatus, OrganizationType } from 'chaika-frontend-types/frontend-cms';

import { SimpleList } from '@store/models/SimpleList';
import { OrganizationPageTestAttributes } from 'chaika-test-attributes';

export interface OrganizationFilterParams {
    ids?: string[];
    query?: string;
    type?: OrganizationType;
    status?: OrganizationStatus;
    types?: OrganizationType[];
    statuses?: OrganizationStatus[];
}

export class OrganizationList extends SimpleList<OrganizationData, OrganizationFilterParams> {
    public static Name = 'organizationList' as const;

    public practiceList: PracticeList = new PracticeList();

    @observable private organizationPractices = new Map<string, PracticeContactViewModel[]>();
    constructor(params?: OrganizationData[]) {
        super(params);
        makeObservable(this);

        this.filterParams = {
            statuses: [OrganizationStatus.Active],
        };
    }

    public getOrganizationPractices(organizationId: string): PracticeContactViewModel[] {
        return this.organizationPractices.get(organizationId) ?? [];
    }

    public isOrganizationPracticesSet(organizationId: string): boolean {
        return this.organizationPractices.has(organizationId);
    }

    @action
    public setOrganizationPractices(organizationId: string, practices: PracticeContactViewModel[]): void {
        this.organizationPractices.set(organizationId, practices);
    }

    @autobind
    public makeInsuranceOrganizationFilterOptions({
        selectedValue,
        onChange,
    }: {
        selectedValue?: string | number;
        onChange(...args: any): Promise<void> | void;
    }): SelectProps {
        const organizationTypes = [
            OrganizationType.Organization,
            OrganizationType.InsuranceCompany,
            OrganizationType.Assistance,
            OrganizationType.Charity,
        ];

        const options = this.getFilteredValues({
            types: organizationTypes,
            status: OrganizationStatus.Active,
        }).map(organization => ({
            label: organization.name,
            value: organization.id,
        }));

        return {
            options,
            selectedValue,
            onChange,
            dataTest: OrganizationPageTestAttributes.InsuranceOrganizationSelect,
        };
    }

    protected filterValue(
        value: OrganizationData,
        { type, ids, status, statuses, types, query }: OrganizationFilterParams,
    ): boolean {
        return (
            (this.filterFieldValueIncludesString(value, query, 'name') ||
                this.filterFieldValueIncludesString(value, query, 'abbreviation')) &&
            this.filterFieldValueByArray(value, type ? [type] : types, 'type') &&
            this.filterFieldValueByArray(value, status ? [status] : statuses, 'status') &&
            this.filterFieldValueByArray(value, ids, 'id')
        );
    }
}

export const organizationList = new OrganizationList();
